import type { Editor } from '@tiptap/core'
import type { Slice } from '@tiptap/pm/model'
import type { EditorView } from '@tiptap/pm/view'
import type { EntityID } from '@wovin/core/applog'
import type { Accessor, Component, JSX, Setter } from 'solid-js'
import type { BlockVM } from '../data/VMs/BlockVM'
import type { RelationVM } from '../data/VMs/RelationVM'
import type { FakeBlock } from './BlockTree'
import { useSearchParams } from '@solidjs/router'
import Image from '@tiptap/extension-image'
import { queryAndMap } from '@wovin/core'
import { Logger } from 'besonders-logger'
import classNames from 'classnames'
import { format } from 'date-fns/format'
import { parseISO } from 'date-fns/parseISO'
import { pick } from 'lodash-es'
import { stringify } from 'safe-stable-stringify'
import { createEffect, createMemo, createSignal, For, on, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { createTiptapEditor, useEditorJSON } from 'solid-tiptap'
import { useAgent } from '../data/agent/AgentState'
import { useBlockContext } from '../data/block-ui-helpers'
import { createTreeItemKeydownHandler } from '../data/block-ui-helpers-keydown'
import {
	copyToClipboard,
	createTreeItemPasteHandler,
	getPlainTextFromClipboard,
	getValidURLFromClipboard,
} from '../data/block-ui-helpers-paste'
import { compareBlockContent } from '../data/block-utils-nowin'
import { REL_DEF } from '../data/data-types'
import { useBlockKeyhandlers } from '../data/keybindings'
import { plaintextStringToTiptap, tiptapToPlaintext } from '../data/note3-utils-nodeps'
import { doesContentMatchSearch, useSearchContext } from '../data/search'
import { useBlk } from '../data/VMs/BlockVM'
import { KnownFullAttrs } from '../data/VMs/utils-typemap'
import { MirrorBulletIcon } from '../icons/MirrorBulletIcon'
import { activePlugins } from '../plugins/init'
import { collectHiddenExtraPropPrefixes } from '../plugins/registry'
import { useAppSettings } from '../ui/app-settings'
import { useBlockVM, useCurrentThread, useReadOnlyState, useRelationVM } from '../ui/reactive'
import {
	devMode,
	EventDebugger,
	focusViewOnBlock,
	isBlockSelected,
	onClickOrLongPress,
	toggleBlockSelection,
	useLocationNavigate,
	useZenMode,
} from '../ui/utils-ui'
import { AudioPlayer } from './AudioPlayer'
import { BlockSettingsRow } from './BlockSettings'
import { BulletMenu } from './BulletMenu'
import { CopyableSpan, DynamicColored, Iconify, IconifyNames, useIdHover } from './mini-components'
import { RecordingIndicator } from './RecordingIndicator'
import {
	baseExtensions,
	CreateTokenHidingExtension,
	SelectionDetectLinkHandler as CustomSelectionHandler,
	htmlToTiptap,
	TagHighlightExtension,
	WordInfoExtension,
} from './TipTapExtentions'
import { TiptapMenuWrapper } from './TiptapMenuWrapper'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars
// scale: relVM?.isMirror ? 40 : blkVM.isReply ? 70 : (blkVM.isTodo ? 100 : 150) }

export const blockIconNameHooks = [
	function mirrorBlockIconName(blockOrFake: BlockVM | FakeBlock, relVM?: RelationVM, likelyKids = false) {
		return relVM?.isMirror
			? (likelyKids
				? { icon: <MirrorBulletIcon />, name: IconifyNames['diamond-duotone'], scale: 60 }
				: { name: IconifyNames['diamond-fill'], scale: 35 })
			: {}
	},
	function replyBlockIconName(blockOrFake: BlockVM | FakeBlock, _relVM?: RelationVM, likelyKids = false) {
		return (blockOrFake as any)?.isReply // HACK: BlockVM doesn't know about view context (but for fake block it's good to be able to set it)
			? (likelyKids
				? { name: IconifyNames['arrow-bend-double-up-left-bold'], scale: 70 }
				: { name: IconifyNames['arrow-bend-down-right-bold'], scale: 70 })
			: {}
	},
	function todoBlockIconName(blockOrFake: BlockVM | FakeBlock, _relVM?: RelationVM, _likelyKids = false) {
		return blockOrFake?.isTodo
			? (
				blockOrFake?.isTodoDone
					? { name: 'check-square-thin', scale: 100 }
					: { name: 'square-thin', scale: 100 }
			)
			: {}
	},
]

type ExtraPropsState = ReturnType<typeof useExtraPropsState>

function useExtraPropsState(blockOrFake: BlockVM | FakeBlock, hasContent: () => boolean) {
	const appSettings = useAppSettings()
	const [zenMode] = useZenMode()
	const [expanded, setExpanded] = createSignal(false)

	const attrs = createMemo(() => {
		if (!blockOrFake || !('entityThread' in blockOrFake)) return []
		const blkVM = blockOrFake as BlockVM

		// Query all attributes (at, vl) from the entity thread, excluding
		// known block-internal attrs (block/content, block/type, etc.).
		const results = queryAndMap(
			blkVM.entityThread,
			{ at: '?attr', vl: '?value' },
			{ attr: 'attr', value: 'value' } as const,
		) as Array<{ attr: string; value: any }>

		const attrMap = new Map<string, any>()
		for (const { attr, value } of results) {
			if (KnownFullAttrs.Block.includes(attr)) continue
			attrMap.set(attr, value) // last write wins per attribute
		}

		return Array.from(attrMap.entries()).map(([at, value]) => {
			const displayValue = typeof value === 'string' && value.length > 50
				? `${value.substring(0, 47)}...`
				: value
			const fullValue = typeof value === 'string' ? value : JSON.stringify(value)
			return { name: at, displayValue, fullValue }
		})
	})

	// Group attributes by namespace prefix (everything up to and including the last `/`).
	// `structuredTranscript/processedAt` + `structuredTranscript/model` collapse into a single
	// `structuredTranscript/{processedAt · model}` segment.
	const groups = createMemo(() => {
		const grouped = new Map<string, { displayValue: any; fullValue: string; name: string; suffix: string }[]>()
		for (const attr of attrs()) {
			const slashIdx = attr.name.lastIndexOf('/')
			const prefix = slashIdx >= 0 ? attr.name.substring(0, slashIdx + 1) : ''
			const suffix = slashIdx >= 0 ? attr.name.substring(slashIdx + 1) : attr.name
			if (!grouped.has(prefix)) grouped.set(prefix, [])
			grouped.get(prefix)!.push({ ...attr, suffix })
		}
		return Array.from(grouped.entries()).map(([prefix, items]) => ({ items, prefix }))
	})

	// Count of attrs NOT hidden by plugin-registered prefixes (for blocktree visibility).
	// The full `attrs` list is unfiltered — used by the extra-props chips display.
	// This separate count lets the blocktree hide blocks that have nothing but hidden attrs.
	const nonHiddenAttrsCount = createMemo(() => {
		const prefixes = collectHiddenExtraPropPrefixes()
		if (prefixes.length === 0) return attrs().length
		return attrs().filter(a => !prefixes.some(p => a.name.startsWith(p))).length
	})

	// Asset blocks (blob/cid) render their own UI element (e.g. audio player) —
	// an empty title doesn't mean "metadata-dominated", so they follow the
	// normal display-mode setting instead of the reveal-more-when-empty rule.
	const isAssetBlock = createMemo(() => attrs().some(a => a.name === 'blob/cid'))

	const mode = createMemo<'condensed' | 'full' | 'indicator'>(() => {
		if (expanded()) return 'full'
		// Empty blocks are dominated by their metadata; reveal more by default.
		// Zen still keeps things minimal (condensed instead of full).
		if (!hasContent() && !isAssetBlock()) return zenMode() ? 'condensed' : 'full'
		if (zenMode()) return 'indicator'
		return appSettings.extraPropsDisplay ?? 'condensed'
	})

	return { attrs, expanded, groups, mode, setExpanded, nonHiddenAttrsCount }
}

const ExtraPropsIndicator: Component<{
	state: ExtraPropsState
	hasContent: boolean
}> = (props) => {
	const opacity = () => props.hasContent ? 'opacity-50' : 'opacity-70'
	return (
		<wa-tag
			data-extra-props
			size='s'
			class={classNames(opacity(), 'shrink-0 cursor-pointer rounded transition-opacity duration-200 hover:opacity-100')}
			style={{ '--wa-input-height-small': '1.4rem', '--wa-button-font-size-small': '0.6rem' }}
			title={`${props.state.attrs().length} properties: ${props.state.attrs().map(a => a.name).join(', ')}`}
			onClick={(e) => {
				e.stopPropagation()
				props.state.setExpanded(true)
			}}
		>
			<span class='inline-flex items-center gap-1'>
				<Iconify name='dots-three-circle' scale={90} />
				{props.state.attrs().length}
			</span>
		</wa-tag>
	)
}

const ExtraPropsList: Component<{
	state: ExtraPropsState
	hasContent: boolean
}> = (props) => {
	const opacity = () => props.hasContent ? 'opacity-50' : 'opacity-70'
	return (
		<>
			<Show when={props.state.mode() === 'full'}>
				<div
					data-extra-props
					class='flex flex-wrap items-center'
					style={{ gap: '0.125rem' }}
					onClick={e => e.stopPropagation()}
				>
					<Show when={props.state.expanded()}>
						<wa-tag
							size='s'
							class='cursor-pointer rounded opacity-60 transition-opacity duration-200 hover:opacity-100'
							style={{ '--wa-input-height-small': '1.6rem', '--wa-button-font-size-small': '0.6rem' }}
							title='Collapse extra properties'
							onClick={(e) => {
								e.stopPropagation()
								props.state.setExpanded(false)
							}}
						>
							<Iconify name='x' scale={80} />
						</wa-tag>
					</Show>
					<For each={props.state.attrs()}>
						{attr => (
							<wa-tag
								size='s'
								class={classNames(opacity(), 'rounded transition-opacity duration-200 hover:opacity-100')}
								style={{ '--wa-input-height-small': '1.6rem', '--wa-button-font-size-small': '0.6rem' }}
								title={`${attr.name}\n${attr.fullValue}`}
							>
								<CopyableSpan value={attr.name}>{attr.name}</CopyableSpan>
								<span class='select-text'>:&nbsp;</span>
								<CopyableSpan value={attr.fullValue}>{attr.displayValue}</CopyableSpan>
							</wa-tag>
						)}
					</For>
				</div>
			</Show>

			<Show when={props.state.mode() === 'condensed'}>
				<div
					data-extra-props
					class={classNames(
						opacity(),
						'flex flex-wrap cursor-pointer select-text items-baseline gap-x-2 text-xs transition-opacity duration-200 hover:opacity-100',
					)}
					title='Click to expand all properties'
					onClick={(e) => {
						e.stopPropagation()
						props.state.setExpanded(true)
					}}
				>
					<For each={props.state.groups()}>
						{group => (
							<span>
								<span class='opacity-70'>{group.prefix}</span>
								<Show
									when={group.items.length > 1}
									fallback={<span title={`${group.items[0].name}\n${group.items[0].fullValue}`}>{group.items[0].suffix}</span>}
								>
									<span class='opacity-70'>{'{'}</span>
									<For each={group.items}>
										{(item, i) => (
											<>
												<Show when={i() > 0}>
													<span class='mx-1 opacity-50'>·</span>
												</Show>
												<span title={`${item.name}\n${item.fullValue}`}>{item.suffix}</span>
											</>
										)}
									</For>
									<span class='opacity-70'>{'}'}</span>
								</Show>
							</span>
						)}
					</For>
				</div>
			</Show>
		</>
	)
}

export const BlockItem: Component<{
	blockID: EntityID | null
	fake?: FakeBlock
	relationID: EntityID | null
	hasKids: boolean
	readOnly?: boolean // ? kind of redundant with thread context readonly
	isExpanded: Accessor<boolean>
	setExpanded: Setter<boolean> | ((newVal: boolean) => any)
}> = (props) => {
	const { blockID, fake } = props // assumed static
	const debugName = blockID ?? JSON.stringify(fake)
	if (!blockID && !fake) {
		throw ERROR(`<Block> with falsy blockID`, blockID)
	}
	VERBOSE(`[BlockItem#${debugName}] created`)

	const { locnav, location, navigate } = useLocationNavigate()
	const [searchParams, _setSearchParams] = useSearchParams()
	const previewFromUrl = createMemo(() => {
		DEBUG(`searchParams:`, { ...searchParams })
		return searchParams.preview
	})

	const { isExpanded, setExpanded } = props // accessor/setter, so keeps reactivity
	const thread = useCurrentThread()

	const blockContext = useBlockContext()
	const parentContext = blockContext?.parentContext
	// const relation = props.relationID && useRelation(props.relationID)
	const relVM = props.relationID && useRelationVM(props.relationID)
	const blkVM = fake ? undefined : useBlk(blockID)
	const parentVM = relVM ? useBlk(relVM.childOf) : null
	const blockOrFake = fake ?? blkVM
	const ag = useAgent().ag
	const isDeleted = createMemo(() => (blockOrFake as BlockVM).isDeleted)
	const threadReadOnly = useReadOnlyState()
	const readOnly = createMemo(() => props.readOnly || !!props.fake || threadReadOnly() || isDeleted())

	const [search] = useSearchContext()
	const shouldHide = createMemo(() => !fake && search() && !doesContentMatchSearch(blkVM.contentPlaintext ?? '', search())) // HACK
	const [blockSettings, setBlockSettings] = createSignal(false)
	function toggleBlockSettings() {
		setBlockSettings(!blockSettings())
	}
	let blockRef: HTMLDivElement
	let editorRef: HTMLDivElement
	let contentRef: HTMLDivElement
	useBlockKeyhandlers(() => ({ blockRef, editorRef }))
	// TODO needs reactivity support:
	const pasteHandler = fake ? EventDebugger : createTreeItemPasteHandler(relVM, blkVM)
	// ? those too?
	const keyDownHandler = createTreeItemKeydownHandler(blockID, props.relationID, isExpanded, setExpanded, ag)
	const bulletIconProps = createMemo(() => {
		const likelyKids = props.hasKids || (blockOrFake as any)?.kidRelations?.length
		// HACK full object with name and scale (or even a jsx icon) could be returned from the hooks
		const iconProps = {
			name: likelyKids ? 'dot-duotone' : 'dot-bold',
			scale: 150,
			icon: undefined,
		}
		for (const eachFx of blockIconNameHooks) {
			const { name, scale, icon } = eachFx(blockOrFake, relVM, likelyKids) as any
			iconProps.name = name ?? iconProps.name
			iconProps.scale = scale ?? iconProps.scale
			iconProps.icon = icon ?? iconProps.icon
			if (icon) DEBUG('svgCustomIcon', { icon })
		}
		return iconProps
	})

	const [clickCount, setClickCount] = createSignal(0)
	let clickTimeout: ReturnType<typeof setTimeout> | null = null
	const DOUBLE_CLICK_DELAY = 250
	const AUTOEXPAND_DEPTH = 3

	const onExpandClick = (event: MouseEvent, long: boolean) => {
		event.preventDefault()
		VERBOSE.force({ event, onClickOrLongPress, clickCount: clickCount() })
		if (!long && event.button !== 0) return // ignore right/middle-click (button check; touch uses button=0)
		if (!(event as PointerEvent).isPrimary) return // ignore extra touch points
		if (event.button === 1 || long) {
			focusViewOnBlock({ id: blockID }, locnav)
			return
		}

		// Clear existing timeout
		if (clickTimeout) clearTimeout(clickTimeout)

		// Increment click count
		const newClickCount = clickCount() + 1
		setClickCount(newClickCount)

		clickTimeout = setTimeout(() => {
			if (newClickCount > 1) {
				// better not to setExpanded as this will set relation applogs
				// and we only want a transient auto expand situation
				// setExpanded(targetState)

				// Notify children to recursively expand/collapse
				// This is done via a block context signal
				if (blockContext?.autoExpandDepth()) {
					blockContext?.setAutoExpandDepth(0)
				} else {
					blockContext?.setAutoExpandDepth(AUTOEXPAND_DEPTH)
				}
			} else {
				VERBOSE.force('// Single-click: toggle just this block')
				setExpanded(!isExpanded())
			}
			// Reset click count
			setClickCount(0)
		}, DOUBLE_CLICK_DELAY)
	}

	// Listen for recursive expand events from parent
	createEffect(() => {
		if (!blockRef) return
		const handleRecursiveExpand = (event: Event) => {
			VERBOSE.force('// Custom recursiveExpandChange ', { event })
			const customEvent = event as CustomEvent<{ shouldExpand: boolean }>
			setExpanded(customEvent.detail.shouldExpand)
		}
		blockRef.addEventListener('recursiveExpandChange', handleRecursiveExpand)
		onCleanup(() => blockRef?.removeEventListener('recursiveExpandChange', handleRecursiveExpand))
	})

	// Attach onClickOrLongPress to content wrapper in readonly mode.
	// Bail when the pointer target is inside an extra-props chip — those have their
	// own click handlers and shouldn't double-trigger block expand/collapse.
	createEffect(() => {
		if (!readOnly() || !contentRef) return
		onClickOrLongPress(contentRef, () => (e, long) => {
			if ((e.target as Element | null)?.closest('[data-extra-props]')) return
			onExpandClick(e, long)
		})
		// onCleanup is called internally by onClickOrLongPress
	})
	// wip prefix rendering placeholder
	const isPrefixNeeded = createMemo(() => false && (blockContext as any)?.prefix || (parentContext as any)?.prefix)
	const prefix = createMemo(() => {
		if (!isPrefixNeeded()) return ''

		if (parentVM) DEBUG('prefix', { parentVM, blkVM })
		const latestLog = blkVM.entityThread.latestLog
		const firstLog = blkVM.entityThread.firstLog
		if (!latestLog || !firstLog) {
			DEBUG('prefix: missing logs', { latestLog, firstLog })
			return ''
		}
		const latestTs = parseISO(latestLog.ts)
		const firstTs = parseISO(firstLog.ts)
		const creationPredicate = "date:c(ddMMM<y@H'h')"
		const yearBeforeThis = format(firstTs, 'y') === format(new Date(), 'y') ? '2025 ' : format(firstTs, 'y ')
		const prefix = `${format(firstTs, `${yearBeforeThis}ddMMM@H'h'`)}`.toLocaleLowerCase()
		return prefix
	})

	// Check if block has content to display
	const hasContent = createMemo(() => {
		const content = blockOrFake?.content
		return content !== null && content !== undefined && (typeof content === 'string' || Object.keys(content || {}).length > 0)
	})
	// Check if block has content to display
	const isHeading = createMemo(() => {
		const content = blockOrFake?.content
		VERBOSE({ content })
		return content !== null && content !== undefined && (content.content?.[0]?.type === 'heading')
	})
	// Check if there are unknown attributes to render
	const hasUnknownAttributes = createMemo(() => {
		if (fake) return false
		const blkVM = blockOrFake as BlockVM
		return blkVM.entityThread.applogs.some(log => !KnownFullAttrs.Block.includes(log.at))
	})

	const extraPropsState = useExtraPropsState(blockOrFake, hasContent)

	// Determine if we should show the content editor
	// Hide editor if: no contentApplog AND has unknown attributes (regardless of readOnly)
	// Otherwise show if: has content OR not readOnly
	const shouldShowContent = createMemo(() => {
		if (!fake && !(blockOrFake as BlockVM).contentApplog && hasUnknownAttributes()) return false
		if (hasContent()) return true
		if (!readOnly()) return true
		return false
	})

	// Blocktree visibility: hide the block when there's no content AND no
	// non-hidden extra-props attributes AND no kids.
	// The extra-props chips ("applog view at the bottom") still show all attrs
	// unfiltered — this only controls whether the block row renders in the tree.
	const hasVisibleContentOrAttrs = createMemo(() => shouldShowContent() || extraPropsState.nonHiddenAttrsCount() > 0 || props.hasKids)

	const isSelected = createMemo(() => isBlockSelected(blockID))

	const handleBlockClick = (evt: MouseEvent) => {
		if (evt.shiftKey && blockID && !readOnly()) {
			evt.preventDefault()
			evt.stopPropagation()
			toggleBlockSelection(blockID)
		}
	}

	return (
		<div
			class='block-content'
			data-info={fake ? `smartResult:${parentContext?.smartList?.query.source}` : `block:${blockID}`}
		>
			<div
				ref={blockRef}
				flex='~ items-start'
				data-block-id={blockID}
				class={classNames(
					shouldHide() ? 'opacity-60' : '',
					isSelected() && 'bg-blue-900 bg-opacity-20',
					!hasVisibleContentOrAttrs() && 'hidden',
				)}
				onClick={handleBlockClick}
			>
				{/* <Show when={!shouldHide()}> */}
				{/* <Show when={blockOrFake.type === 'smartlist' || props.hasKids}> */}
				<div
					class='order-9 cursor-pointer color-gray-400 sm:order-0'
					flex='shrink-0'
					// onMouseDown={onExpandClick} // mousedown to not loose focus - https://stackoverflow.com/a/43627784
					use:onClickOrLongPress={onExpandClick}
				>
					<Iconify
						class={classNames([
							'color-gray-400 duration-300 transition-property-[transform]',
							isExpanded() ? ' rotate-90' : ' rotate-0',
						])}
						size='6 6'
						scale={60}
						name={props.hasKids ? 'caret-right-bold' : null}
					{...({} as any)}
					/>
				</div>
				{/* </Show> */}
				<BulletMenu
					class='mr-1 shrink-0'
					blockID={blockID}
					customIcon={(bulletIconProps() as any)?.icon}
					iconProps={bulletIconProps() as any}
					showBlockSettings={toggleBlockSettings}
					onlyShowOnHover={isHeading()}
						{...({} as any)}
				/>
				<Show when={isPrefixNeeded()}>
					{prefix()}
					<Iconify name={'number-circle-one-fill' as any} scale={120} />
				</Show>
				<Show when={blockOrFake.type === 'smartlist'}>
					<wa-tag
						size='s'
						variant='brand'
						onClick={() => setBlockSettings(!blockSettings())}
					>
						<Iconify name='list-magnifying-glass' scale={120} />
					</wa-tag>
					<div mx-1 h-6 opacity-40 flex='~ items-center'>
						{/* <span text-xs>{parentContext?.smartList?.query.source}</span> */}
						<Show when={parentContext?.smartList?.query.source}>
							<span text-xs title={parentContext?.smartList?.query.source} cursor-default>... AND</span>
						</Show>
					</div>
				</Show>
				<div ref={contentRef} flex='~ col grow-1 shrink-1' class={readOnly() && props.hasKids ? 'cursor-pointer' : undefined}>
					<div flex='~ row items-center gap-2'>
						<Show when={shouldShowContent()}>
							<EditableContent
								blockID={blockID}
								ref={editorRef}
								fake={fake ?? (isDeleted() && {
									content: htmlToTiptap('<em>deleted</em>'),
								})}
								relationID={props.relationID}
								data-hidden-tags={stringify(blockContext.hiddenTags?.() ?? null)}
								onPaste={pasteHandler as any}
								onKeyDown={keyDownHandler as any}
								readOnly={readOnly()}
								flex='grow-1 shrink-1'
								class={classNames(
									isDeleted() && 'text-red opacity-80',
								)}
							/>
						</Show>
						<Show when={extraPropsState.mode() === 'indicator' && extraPropsState.attrs().length > 0}>
							<ExtraPropsIndicator state={extraPropsState} hasContent={hasContent()} />
						</Show>
					</div>
					<AudioPlayer blockID={blockID} /> {/* compact row incl. actions dot menu + transcript toggle */}
					<RecordingIndicator blockID={blockID} />
					<Show when={extraPropsState.mode() !== 'indicator' && extraPropsState.attrs().length > 0}>
						<ExtraPropsList state={extraPropsState} hasContent={hasContent()} />
					</Show>
				</div>
				{
					/* <Show when={blockOrFake.type === 'smartlist'}>
					<SmartListFilters kidCount={parentContext?.smartList()?.blocks.length} />
				</Show> */
				}
				<Show when={devMode()}>
					<span
						flex='inline col items-stretch'
						shrink-0
						ml-1
						font-size-2
						font-mono
						text-right
					>
						<Show when={blockID}>
							<div {...(useIdHover('block', blockID))} flex='~ items-stretch' transition='[opacity] duration-300'>
								B&thinsp;
								<DynamicColored
									text={blockID}
									class='cursor-pointer'
									title='click to copy'
									onClick={() => copyToClipboard(blockID)}
								/>
							</div>
						</Show>
						<Show when={props.relationID}>
							<div {...(useIdHover('block', props.relationID))} transition='[opacity] duration-300' flex='~ items-stretch'>
								R&thinsp;{' '}
								<DynamicColored
									text={props.relationID}
									class='cursor-pointer'
									title={`${JSON.stringify(pick(relVM, Object.values(REL_DEF._attrs)), undefined, 2)}\nClick to copy ID`}
									onClick={() => copyToClipboard(props.relationID)}
								/>
							</div>
							<div {...(useIdHover('block', relVM?.after))} transition='[opacity] duration-300' flex='~ items-stretch'>
								↑&thinsp;{' '}
								<DynamicColored
									text={relVM?.after ?? 'null'}
									class={relVM?.after && 'cursor-pointer'}
									title={relVM?.after && 'click to copy'}
									onClick={relVM?.after && (() => copyToClipboard(relVM.after))}
								/>
							</div>
						</Show>
					</span>
				</Show>
				{/* </Show> */}
			</div>

			<Show when={blockSettings()}>
				{/* HACK: prop drilling */}
				<BlockSettingsRow blockID={blockID} thread={thread} blockOrFake={blockOrFake} />
			</Show>
		</div>
	)
}

// HACK: I'm not sure how to get from dom node to Editor in another way
export const editorMap = new Map<HTMLDivElement, Editor>()

// const CustomShortcuts = Extension.create({
// 	name: 'CustomShortcuts',
// 	addKeyboardShortcuts() {
// 		return {
// 			// HACK: move default Enter behaviour to Shift-Enter (as our Enter creates a new block)
// 			'Shift-Enter': ({ editor }) =>
// 				// from: https://github.com/ueberdosis/tiptap/blob/39cf6979c49e953118bbbe4b894a1dc296128932/packages/core/src/extensions/keymap.ts#L49C25-L54C7
// 				editor.commands.first(({ commands }) => [
// 					() => commands.newlineInCode(),
// 					() => commands.createParagraphNear(),
// 					() => commands.liftEmptyBlock(),
// 					() => commands.splitBlock(),
// 				]),
// 		}
// 	},
// })

export const EditableContent: Component<
	{
		readOnly?: boolean
		blockID: EntityID | null
		fake?: FakeBlock
		relationID: EntityID | null
		// blockVM: BlockVM
		// block: typeof BLOCK
		// vintageContent: Accessor<string>
		onKeyDown: (evt: React.KeyboardEvent<HTMLDivElement>) => Promise<void>
		onPaste: (view: EditorView, event: ClipboardEvent, slice: Slice) => boolean | void
	} & JSX.HTMLAttributes<HTMLSpanElement>
> = (fullProps) => {
	const [props, otherProps] = splitProps(fullProps, ['readOnly', 'blockID', 'relationID', 'fake', 'onKeyDown', 'onPaste']) // (i) this function is still not properly reactive to those, though
	const readOnlyState = useReadOnlyState()
	const readOnly = createMemo(() => props.readOnly || readOnlyState())
	VERBOSE(`<EditableContent#${props.blockID}> created`, { props, otherProps, ro: readOnly(), ds: useCurrentThread() })
	const blockVM = props.fake ?? useBlockVM(props.blockID)
	const [isEditing, setEditing] = createSignal(null)
	const [selectionLink, setSelectionLink] = createSignal<{ href: string } | null>(null)

	let ref!: HTMLDivElement
	// let menuRef!: HTMLDivElement
	Image.configure({
		allowBase64: true,
	})
	const initialContent = blockVM.content

	VERBOSE(`[Content#${props.blockID}] initial`, initialContent)

	const fakeContent = props.fake?.content && (typeof props.fake.content === 'string'
		? plaintextStringToTiptap(props.fake.content)
		: props.fake.content)
	// TODO decorate or somehow hide the outer (and maybe recursive) smartlist tokens
	// ... somehow based on parent block context isTokenHidden prop of smartlist
	const blockContext = useBlockContext()
	const parentContext = blockContext?.parentContext

	const extensionsMemo = createMemo(() => {
		const extensions = [
			...baseExtensions,
			Image,
			// CustomShortcuts,
			TagHighlightExtension,
			WordInfoExtension,
			CustomSelectionHandler(selectionLink, setSelectionLink),
			// BubbleMenu.configure({
			// 	element: menuRef!,
			// }),
			...activePlugins.flatMap(p => p.tiptapExtensions),
		]

		const hiddenTags = parentContext?.hiddenTags()
		if (hiddenTags) {
			VERBOSE('CreateTokenHidingExtension', { hiddenTags })
			extensions.push(CreateTokenHidingExtension(hiddenTags))
		}
		return extensions
	})

	const editor = createTiptapEditor(() => {
		const editorProps = {
			element: ref!,
			editable: !readOnly(),
			editorProps: {
				attributes: {
					//   class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
					class: 'focus:outline-none -my-3',
				},
				// handlePaste: CustomStackedPasteHandler([props.onPaste /* () => LOG('handler1'), () => LOG('handler2') */]),
				handlePaste: props.onPaste,
			},
			extensions: extensionsMemo(),
			content: fakeContent ?? initialContent,
		}
		DEBUG(`<EditableContent.tiptap> props`, editorProps)
		return editorProps
	})
	onMount(() => {
		editorMap.set(ref!, editor())
	})
	onCleanup(() => {
		editorMap.delete(ref!)
	})

	const contentJson = useEditorJSON(editor)
	if (!readOnly()) {
		createEffect(on(() => contentJson(), (newContent) => {
			DEBUG(`[EditableContent#${props.blockID}].htmlUpdate]`, newContent, { blockVM })

			if (!compareBlockContent(newContent, blockVM.content)) {
				DEBUG('[EditableContent#${props.blockID}].htmlUpdate] changed', { old: blockVM.content, new: newContent /* , parsed  */ })

				blockVM.content = newContent // this is a json object

				setEditing(setTimeout(() => setEditing(null), 1000)) // HACK: unify with BlockVM.isEditing
				// persistBlockContent(block.en, spanText)
			}
		}, { defer: true })) // (i) still called on first load (I think)
	}
	createEffect(on(() => blockVM.content, (newContent) => {
		DEBUG(`[EditableContent#${props.blockID}] newContent:`, newContent, { ref, isEditing: (blockVM as any).isEditing })
		if (!isEditing()) {
			if (!compareBlockContent(contentJson(), newContent)) {
				editor().commands.setContent(newContent)
			}
		}
	}, { defer: true })) // don't call on first load

	const handleLinkButton = async (_evt: PointerEvent) => {
		if (editor().isActive('link')) {
			DEBUG('removing', editor().getAttributes('link').href)
			// (i) tries to extend to
			editor().chain().focus().extendMarkRange('link').unsetLink().run()
			setSelectionLink(null)
		} else {
			const maybeValidURL = await getValidURLFromClipboard()
			if (maybeValidURL) {
				VERBOSE('setting link in tiptap', maybeValidURL)
				editor().chain().focus().toggleLink({ href: maybeValidURL }).run()
				setSelectionLink({ href: maybeValidURL })
				// ? i guess focus is needed cuz the mini menu steals it on click ?
			} else {
				WARN('Link Button clicked but no link found', await getPlainTextFromClipboard())
			}
		}
	}
	if (props.fake) VERBOSE('fake:', props.fake)
	return (
		<>
			<div
				{...otherProps}
				onKeyDown={props.onKeyDown}
				ref={ref}
				id={props.fake
					? `fake:${(props.fake as any).smartQuery?.text ?? tiptapToPlaintext(props.fake.content)
						/* ?? props.fake.content?.content?.[0]?.content?.[0]?.text */
					}`
					: `block-${(blockVM as any).en}`}
				flex='grow-1'
				min-w-0
			/>
			<TiptapMenuWrapper {...({} as any)}
				editor={editor()}
				tippyOptions={{ duration: 300 }}
			>
				{/* shouldShow={({ editor, state, view, from, to }) => { }} */}
				{/* FIXME: tooltips not shown */}
				<wa-button-group
					label='Formatting'
					flex
					drop-shadow-lg
					style={{ 'background-color': 'hsl(240 5% 27.6%)', 'border': '1px solid hsl(240 5% 27.6%)', 'border-radius': '0.25rem' }}
				>
					<wa-tooltip content='Bold'>
						<wa-button size='s' onClick={() => editor().chain().focus().toggleBold().run()}>
							<Iconify slot='start' scale={140} name='text-b-bold' />
						</wa-button>
					</wa-tooltip>
					<wa-tooltip content='Italic'>
						<wa-button size='s' onClick={() => editor().chain().focus().toggleItalic().run()}>
							<Iconify slot='start' scale={140} name='text-italic-bold' />
						</wa-button>
					</wa-tooltip>
					<wa-tooltip content='Underline'>
						<wa-button size='s' onClick={() => editor().chain().focus().toggleUnderline().run()}>
							<Iconify slot='start' scale={140} name='text-underline-bold' />
						</wa-button>
					</wa-tooltip>
					<wa-tooltip content='Strikethrough'>
						<wa-button size='s' onClick={() => editor().chain().focus().toggleStrike().run()}>
							<Iconify slot='start' scale={140} name='text-strikethrough-bold' />
						</wa-button>
					</wa-tooltip>
					<wa-tooltip content='Regular style'>
						<wa-button size='s' onClick={() => editor().chain().focus().unsetAllMarks().run()}>
							<Iconify slot='start' scale={140} name='eraser-fill' />
						</wa-button>
					</wa-tooltip>
					<div h-full style={{ 'background-color': 'hsl(240 5% 27.6%)', 'width': '2px' }}>
						{/* divider */}
					</div>

					<Show when={!selectionLink()}>
						<wa-tooltip content='Link'>
							<wa-button size='s' onClick={handleLinkButton}>
								<Iconify slot='start' scale={140} name='link-bold' />
							</wa-button>
						</wa-tooltip>
					</Show>
					<Show when={selectionLink()}>
						<wa-tooltip content={`Remove link: ${selectionLink().href}`}>
							<wa-button size='s' onClick={handleLinkButton}>
								<Iconify slot='start' scale={140} name='link-break-bold' />
							</wa-button>
						</wa-tooltip>
						<a href={selectionLink().href} target='_blank'>
							{/* <wa-tooltip content={'Open: ' + selectionLink().href}> */}
							<wa-button size='s'>
								{/* onClick={() => window.open(selectionLink().href, '_blank')} */}
								<Iconify slot='start' scale={140} name='arrow-square-out' />
							</wa-button>
						</a>
						{/* </wa-tooltip> */}
					</Show>
				</wa-button-group>
			</TiptapMenuWrapper>
		</>
	)
	// return (
	// 	<span
	// 		{...otherProps}
	// 		focus-outline-none
	// 		role='textbox'
	// 		onBlur={onBlur}
	// 		onKeyUp={onType}
	// 		onKeyDown={local.onKeyDown}
	// 		onPaste={local.onPaste}
	// 		contenteditable
	// 		p-1
	// 		cursor-text
	// 		min-w-16
	// 		ref={contentElem}
	// 		id={'block-' + blockVM.enID}
	// 		break-all // TODO: should be only break-words, but doesn't work
	// 		hyphens-auto
	// 	>
	// 		{/* w-full w-min-content */}
	// 		{untracked(() => blockVM.content)}
	// 	</span>
	// )
}
